package dev.danko.roblox_craft_ai.shared.model

import kotlinx.serialization.Serializable

/**
 * Short names of works ("Рунный меч") shown instead of their prompts. The server asks a language model for one when a
 * work is created and stores it in `metadata["title"]`; the user can rename it. Until then a name is cut from the
 * prompt with the generator's boilerplate left out.
 */
object AssetTitles {
    const val KEY = "title"
    const val MAX_LENGTH = 40

    /** Phrases the prompt helpers add that say nothing about the object itself. */
    private val BOILERPLATE = listOf(
        "single isolated 3d asset", "isolated 3d asset", "single isolated", "3d asset", "3d model", "game asset",
        "game-ready", "game ready", "roblox", "low poly", "low-poly", "stylized", "high quality", "white background",
        "plain background", "studio lighting", "centered", "full body", "front view", "t-pose", "a-pose", "pbr"
    )

    /** A name the user typed: one line, trimmed, at most [MAX_LENGTH] characters; null when nothing is left. */
    fun clean(raw: String): String? {
        val line = raw.lineSequence().map { it.trim() }.firstOrNull { it.isNotEmpty() } ?: return null
        var text = line
        while (true) {
            val next = text.trim('"', '«', '»', '\'', '*', '`', ' ').trimEnd('.', '!', '?', ',', ';', ':')
            if (next == text) break
            text = next
        }
        if (text.isEmpty()) return null
        return if (text.length <= MAX_LENGTH) text else text.take(MAX_LENGTH).substringBeforeLast(' ').ifEmpty { text.take(MAX_LENGTH) }
    }

    /** A name cut from the prompt: its first meaningful clause, at most five words. */
    fun fallback(prompt: String): String {
        var text = prompt
        for (phrase in BOILERPLATE) text = text.replace(phrase, " ", ignoreCase = true)
        val clause = text.split(',', ';', '.', '\n', ':').map { it.trim(' ', '-', '—') }.firstOrNull { it.length > 1 } ?: prompt.trim()
        val words = clause.split(Regex("\\s+")).filter { it.isNotBlank() }.take(5).joinToString(" ")
        val name = clean(words) ?: return prompt.trim().take(MAX_LENGTH)
        return name.replaceFirstChar { it.uppercase() }
    }
}

/** The work's short name: the stored one, or one cut from the prompt. */
val AssetResponse.displayTitle: String
    get() = metadata[AssetTitles.KEY]?.takeIf { it.isNotBlank() } ?: AssetTitles.fallback(prompt)

@Serializable
data class RenameAssetRequest(val title: String)
